// src/pages/RepartitionCompetiteursPage/DistributionDetailsModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaGripVertical,
  FaMagic,
  FaRandom,
  FaSave,
  FaTimes,
  FaTimesCircle,
  FaTrashAlt,
  FaUndo,
} from "react-icons/fa";

import { updateDistribution } from "../../services/distributionsServices";

const FORMULA_LABELS = {
  seul_dans_distribution: "Seul(e) dans la distribution",
  non_classes: "Non classés (NC)",
  poule_2: "Poule de 2",
  poule_3: "Poule de 3",
  poule_4: "Poule de 4",
  poule_5: "Poule de 5",
  poule_6: "Poule de 6",
  tableau_base_8: "Tableau de base 8",
  tableau_base_16: "Tableau de base 16",
  tableau_base_32: "Tableau de base 32",
  tableau_base_64: "Tableau de base 64",
  tableau_repechage_base_8: "Tableau repêchage base 8",
  tableau_repechage_base_16: "Tableau repêchage base 16",
  tableau_repechage_base_32: "Tableau repêchage base 32",
  tableau_repechage_base_64: "Tableau repêchage base 64",
};

const Chip = ({ children, className = "" }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${className}`}
  >
    {children}
  </span>
);

const IconButton = ({
  children,
  icon: Icon,
  onClick,
  variant = "outline",
  disabled = false,
  type = "button",
  className = "",
}) => {
  const variants = {
    outline:
      "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-white/10 dark:bg-slate-900 dark:text-white dark:hover:bg-white/10",
    brand:
      "border border-orange-500 bg-orange-600 text-white hover:bg-orange-700 shadow-lg shadow-orange-500/20",
    danger:
      "border border-rose-500 bg-rose-600 text-white hover:bg-rose-700 shadow-lg shadow-rose-500/20",
    soft:
      "border border-slate-200 bg-slate-100 text-slate-700 hover:bg-slate-200 dark:border-white/10 dark:bg-slate-800 dark:text-white dark:hover:bg-slate-700",
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`
        inline-flex min-h-10 items-center justify-center gap-2 rounded-xl px-3 py-2
        text-xs font-bold transition disabled:cursor-not-allowed disabled:opacity-60
        ${variants[variant] || variants.outline}
        ${className}
      `}
    >
      {Icon && <Icon className="h-3.5 w-3.5 shrink-0" />}
      {children}
    </button>
  );
};

const shuffle = (arr) => {
  const a = [...arr];

  for (let i = a.length - 1; i > 0; i -= 1) {
    let j;

    if (window.crypto?.getRandomValues) {
      const randomValues = new Uint32Array(1);
      window.crypto.getRandomValues(randomValues);
      j = randomValues[0] % (i + 1);
    } else {
      j = Math.floor(Math.random() * (i + 1));
    }

    [a[i], a[j]] = [a[j], a[i]];
  }

  return a;
};

const safeWeight = (w) => {
  if (w == null || w === "") return "—";

  const n = Number(String(w).replace(",", "."));

  return Number.isNaN(n) ? "—" : `${n.toFixed(1)} kg`;
};

const parseCompetiteursIds = (distribution) => {
  if (!distribution) return [];

  const src = distribution.competiteurs;

  if (Array.isArray(src)) return src;

  try {
    const parsed = JSON.parse(src || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const buildPositionMap = ({ distribution, orderedIds }) => {
  const map = new Map();

  if (!distribution) return map;

  let raw = distribution.position_aleatoire;

  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw || "");
    } catch {
      raw = null;
    }
  }

  if (Array.isArray(raw)) {
    if (raw.length > 0 && typeof raw[0] === "object" && raw[0] !== null) {
      raw.forEach((item, index) => {
        const rawId = item.id ?? item.competiteur_id ?? orderedIds[index];
        const id = Number(rawId) || rawId;
        const pos = Number(item.pos ?? item.position ?? index + 1);

        if (rawId != null && !Number.isNaN(pos)) {
          map.set(id, pos);
        }
      });
    } else {
      orderedIds.forEach((rawId, index) => {
        const id = Number(rawId) || rawId;
        const pos = Number(raw[index]) || index + 1;
        map.set(id, pos);
      });
    }
  } else if (raw && typeof raw === "object") {
    Object.entries(raw).forEach(([key, value]) => {
      const id = Number(key) || key;
      const pos = Number(value);

      if (!Number.isNaN(pos)) {
        map.set(id, pos);
      }
    });
  }

  orderedIds.forEach((rawId, index) => {
    const id = Number(rawId) || rawId;

    if (!map.has(id)) {
      map.set(id, index + 1);
    }
  });

  return map;
};

const DistributionDetailsModal = ({
  isOpen,
  onClose,
  distribution,
  allCompetiteurs = [],
  onDeleteDistribution,
  onSaved,
}) => {
  const drawerRef = useRef(null);
  const dragIndex = useRef(null);

  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const orderedIds = useMemo(
    () => parseCompetiteursIds(distribution),
    [distribution]
  );

  const idToPos = useMemo(
    () => buildPositionMap({ distribution, orderedIds }),
    [distribution, orderedIds]
  );

  const initialRows = useMemo(() => {
    if (!distribution) return [];

    return orderedIds
      .map((rawId) => {
        const id = Number(rawId) || rawId;
        const row = allCompetiteurs.find(
          (competiteur) => String(competiteur.id) === String(id)
        );

        if (!row) return null;

        return {
          ...row,
          _pos: idToPos.get(id) ?? 9999,
          _clubKey: String(row.club_competiteur || "")
            .trim()
            .toLowerCase(),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a._pos - b._pos);
  }, [distribution, orderedIds, allCompetiteurs, idToPos]);

  const distributionDiscipline = useMemo(() => {
    const values = [
      ...new Set(
        initialRows.map((row) => row.discipline_competiteur).filter(Boolean)
      ),
    ];

    if (values.length === 1) return values[0];
    if (values.length > 1) return values.join(" / ");

    return distribution?.discipline || "—";
  }, [initialRows, distribution]);

  const ruleInfo = useMemo(() => {
    const isPool = /^poule_/i.test(
      String(distribution?.formule_repartition || "")
    );

    if (!isPool || rows.length === 0) {
      return {
        isPool: false,
        hasDup: false,
        applied: false,
        clubName: null,
        pair: [],
      };
    }

    const byClub = new Map();

    for (const competitor of rows) {
      const key = competitor._clubKey || "sans-club";

      if (!byClub.has(key)) {
        byClub.set(key, []);
      }

      byClub.get(key).push(competitor);
    }

    const duplicateClubs = [...byClub.entries()].filter(
      ([, competitors]) => competitors.length >= 2
    );

    if (!duplicateClubs.length) {
      return {
        isPool: true,
        hasDup: false,
        applied: false,
        clubName: null,
        pair: [],
      };
    }

    duplicateClubs.sort((a, b) => b[1].length - a[1].length);

    const [, competitors] = duplicateClubs[0];
    const pair = competitors.slice(0, 2);

    const applied =
      pair.length === 2 &&
      rows[0]?.id === pair[0].id &&
      rows[1]?.id === pair[1].id;

    return {
      isPool: true,
      hasDup: true,
      applied,
      clubName: pair[0]?.club_competiteur || competitors[0]?.club_competiteur,
      pair,
    };
  }, [distribution, rows]);

  useEffect(() => {
    if (!isOpen) return;

    setRows(initialRows);
    setSaving(false);
  }, [isOpen, initialRows]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  const onDragStart = (index) => (event) => {
    dragIndex.current = index;
    event.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = () => (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const onDrop = (index) => (event) => {
    event.preventDefault();

    const from = dragIndex.current;

    if (from == null || from === index) return;

    setRows((currentRows) => {
      const next = [...currentRows];
      const [moved] = next.splice(from, 1);
      next.splice(index, 0, moved);
      return next;
    });

    dragIndex.current = null;
  };

  const handleReset = () => {
    setRows(initialRows);
  };

  const handleRandom = () => {
    setRows((currentRows) => shuffle(currentRows));
  };

  const applyClubRule = () => {
    if (!ruleInfo.hasDup || ruleInfo.applied || ruleInfo.pair.length < 2) {
      return;
    }

    const [first, second] = ruleInfo.pair;
    const topIds = new Set([first.id, second.id]);

    setRows((currentRows) => [
      first,
      second,
      ...currentRows.filter((row) => !topIds.has(row.id)),
    ]);
  };

  const handleSave = async () => {
    if (!distribution?.id || saving) return;

    try {
      setSaving(true);

      const newOrderIds = rows.map((row) => row.id);
      const newPositions = rows.map((_, index) => index + 1);

      const payload = {
        id: distribution.id,
        competiteurs: newOrderIds,
        position_aleatoire: newPositions,
      };

      const saved = await updateDistribution(payload);

      if (saved) {
        onSaved?.();
        onClose?.();
      }
    } catch (error) {
      console.error(error);
      alert("Erreur lors de l’enregistrement de la distribution.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !distribution) return null;

  return (
    <div
      className="
        fixed inset-0 z-[99999] flex h-[100dvh] w-full items-end justify-center
        overflow-hidden bg-slate-950/65 backdrop-blur-sm
        sm:items-stretch sm:justify-end
      "
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose?.();
        }
      }}
    >
      <section
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Détails de la distribution"
        className="
          relative flex h-[92dvh] w-full max-w-full flex-col overflow-hidden
          rounded-t-[2rem] border border-white/20 bg-white text-slate-950 shadow-2xl
          dark:border-white/10 dark:bg-slate-950 dark:text-white
          sm:h-full sm:max-w-[960px] sm:rounded-none sm:rounded-l-[2rem]
        "
      >
        <header className="relative shrink-0 overflow-hidden border-b border-white/10 bg-gradient-to-br from-slate-950 via-slate-900 to-orange-950 px-4 py-4 text-white sm:px-6">
          <div className="pointer-events-none absolute -right-16 -top-24 h-56 w-56 rounded-full bg-orange-500/30 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-16 h-56 w-56 rounded-full bg-blue-500/20 blur-3xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-orange-300">
                Distribution
              </p>

              <h2 className="mt-1 text-xl font-bold leading-tight sm:text-2xl">
                Détails & ordre des compétiteurs
              </h2>

              <div className="mt-3 flex flex-wrap gap-2">
                <Chip className="bg-white/10 text-white ring-1 ring-white/15">
                  {FORMULA_LABELS[distribution.formule_repartition] ||
                    distribution.formule_repartition ||
                    "Formule inconnue"}
                </Chip>

                <Chip className="bg-white/10 text-white ring-1 ring-white/15">
                  {distribution.genre === "M"
                    ? "Masculin"
                    : distribution.genre === "F"
                    ? "Féminin"
                    : distribution.genre || "Genre —"}
                </Chip>

                <Chip className="bg-white/10 text-white ring-1 ring-white/15">
                  {distribution.categorie_age || "Cat. âge —"}
                </Chip>

                <Chip className="bg-white/10 text-white ring-1 ring-white/15">
                  {distribution.categorie_poids || "Cat. poids —"}
                </Chip>

                <Chip className="bg-white/10 text-white ring-1 ring-white/15">
                  Tapis {distribution.numero_tapis ?? "—"}
                </Chip>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="
                inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl
                border border-white/10 bg-white/10 text-white transition
                hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300
              "
              aria-label="Fermer"
            >
              <FaTimes className="h-4 w-4" />
            </button>
          </div>
        </header>

        <div className="shrink-0 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur dark:border-white/10 dark:bg-slate-950/95 sm:px-6">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <IconButton icon={FaUndo} onClick={handleReset}>
                Réinitialiser
              </IconButton>

              <IconButton icon={FaRandom} onClick={handleRandom}>
                Mélanger
              </IconButton>

              {ruleInfo.isPool && ruleInfo.hasDup && !ruleInfo.applied && (
                <IconButton icon={FaMagic} onClick={applyClubRule} variant="brand">
                  Règle 1 & 2
                </IconButton>
              )}

              {ruleInfo.applied && (
                <Chip className="bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-300/20">
                  Même club en positions 1 & 2 : {ruleInfo.clubName}
                </Chip>
              )}
            </div>

            <div className="flex flex-wrap gap-2 text-xs font-bold text-slate-500 dark:text-slate-400">
              <span>{rows.length} compétiteur{rows.length > 1 ? "s" : ""}</span>
              <span>•</span>
              <span>{distributionDiscipline}</span>
            </div>
          </div>

          {ruleInfo.isPool && ruleInfo.hasDup && !ruleInfo.applied && (
            <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900 dark:border-amber-400/20 dark:bg-amber-500/10 dark:text-amber-100">
              Attention : plusieurs compétiteurs du club{" "}
              <span className="font-bold">{ruleInfo.clubName}</span> sont dans
              cette poule. Tu peux les placer automatiquement en positions 1 et
              2.
            </div>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 sm:px-5">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-900">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-xs">
                <thead className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  <tr>
                    <th className="w-8 px-2 py-2 text-left"></th>
                    <th className="px-2 py-2 text-left font-bold uppercase tracking-wide">
                      Pos.
                    </th>
                    <th className="px-2 py-2 text-left font-bold uppercase tracking-wide">
                      ID
                    </th>
                    <th className="px-2 py-2 text-left font-bold uppercase tracking-wide">
                      Nom
                    </th>
                    <th className="px-2 py-2 text-left font-bold uppercase tracking-wide">
                      Prénom
                    </th>
                    <th className="px-2 py-2 text-left font-bold uppercase tracking-wide">
                      Club
                    </th>
                    <th className="px-2 py-2 text-left font-bold uppercase tracking-wide">
                      Poids
                    </th>
                    <th className="px-2 py-2 text-left font-bold uppercase tracking-wide">
                      Règle
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {rows.map((competiteur, index) => {
                    const inPair =
                      ruleInfo.applied &&
                      ruleInfo.pair.some((pair) => pair.id === competiteur.id) &&
                      (index === 0 || index === 1);

                    return (
                      <tr
                        key={competiteur.id}
                        draggable
                        onDragStart={onDragStart(index)}
                        onDragOver={onDragOver(index)}
                        onDrop={onDrop(index)}
                        className={`
                          border-t border-slate-100 transition dark:border-white/10
                          ${
                            inPair
                              ? "bg-emerald-50 dark:bg-emerald-500/10"
                              : "hover:bg-slate-50 dark:hover:bg-white/5"
                          }
                        `}
                      >
                        <td className="px-2 py-1.5">
                          <span className="inline-flex cursor-move items-center justify-center text-slate-400">
                            <FaGripVertical className="h-3.5 w-3.5" />
                          </span>
                        </td>

                        <td className="px-2 py-1.5 font-bold">{index + 1}</td>
                        <td className="px-2 py-1.5">{competiteur.id}</td>

                        <td className="px-2 py-1.5 font-bold">
                          {competiteur.nom_competiteur}
                        </td>

                        <td className="px-2 py-1.5">
                          {competiteur.prenom_competiteur}
                        </td>

                        <td
                          className="max-w-[220px] truncate px-2 py-1.5"
                          title={competiteur.club_competiteur}
                        >
                          {competiteur.club_competiteur || "—"}
                        </td>

                        <td className="px-2 py-1.5">
                          <Chip className="bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                            {safeWeight(competiteur.poids_competiteur)}
                          </Chip>
                        </td>

                        <td className="px-2 py-1.5">
                          {inPair ? (
                            <Chip className="bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                              Même club 1–2
                            </Chip>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {rows.length === 0 && (
                    <tr>
                      <td
                        colSpan={8}
                        className="px-3 py-10 text-center text-sm text-slate-500 dark:text-slate-400"
                      >
                        Aucun compétiteur dans cette distribution.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
            Astuce : glisse une ligne pour modifier l’ordre. Le bouton “Mélanger”
            refait un tirage aléatoire local avant enregistrement.
          </p>
        </div>

        <footer className="shrink-0 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur dark:border-white/10 dark:bg-slate-950/95 sm:px-6">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
            {onDeleteDistribution && (
              <IconButton
                icon={FaTrashAlt}
                onClick={() => onDeleteDistribution(distribution)}
                variant="danger"
              >
                Supprimer
              </IconButton>
            )}

            <IconButton icon={FaTimesCircle} onClick={onClose}>
              Fermer
            </IconButton>

            <IconButton
              icon={FaSave}
              onClick={handleSave}
              variant="brand"
              disabled={saving}
            >
              {saving ? "Enregistrement..." : "Enregistrer l’ordre"}
            </IconButton>
          </div>
        </footer>
      </section>
    </div>
  );
};

export default DistributionDetailsModal;